import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Form, Input, InputNumber, Modal,
  Radio, Row, Select, Space, Switch, Tabs, Tag, Typography, message
} from 'antd';
import {
  SaveOutlined, CheckOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { expenseEntryApi } from './expenseEntryApi';
import {
  EXPENSE_TYPES, EXPENSE_TYPE_COLOR, EXPENSE_TYPE_LABEL,
  PAYMENT_MODES, SUPPLY_TYPES, GST_RATES,
  STATUS_COLOR, STATUS_LABEL, formatINR
} from './expenseEntryConstants';
// Stage 6B.7D — audit log + accounting attachments view tabs.
import AuditLogPanel from '../AuditLogPanel';
import AttachmentsPanel from '../AttachmentsPanel';
import '../accounts.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Expense Entry Add/Edit/View modal (Stage 6B.7).
//
// Two posting modes share one modal (paid vs credit). The expenseType
// radio drives which counter-party field appears (cash/bank ledger
// for paid; supplier dropdown for credit). GST split is auto-computed
// from taxable_value × gst_rate × supply_type via the same helper the
// service-layer uses on post.
//
// A live "posting preview" panel renders the exact DR/CR explosion
// the engine will write — same pattern as PaymentVoucherModal.
// Post + Cancel are handled by the parent page so the Approval-MFA
// modal never stacks on top of this one.
// ─────────────────────────────────────────────────────────────────

const READONLY_STATUSES = new Set(['posted', 'cancelled']);

function emptyDefaults() {
  return {
    expenseDate:              dayjs(),
    expenseType:              'paid',
    categoryId:               null,
    supplierId:               null,
    cashBankLedgerAccountId:  null,
    paymentMode:              'bank',
    taxableValue:             0,
    gstApplicable:            false,
    gstRate:                  0,
    supplyType:               'intra_state',
    cgstAmount:               0,
    sgstAmount:               0,
    igstAmount:               0,
    itcEligible:              true,
    totalAmount:              0,
    referenceNo:              '',
    remarks:                  ''
  };
}

function defaultsFor(mode, record) {
  if (mode === 'add' || !record) return emptyDefaults();
  return {
    expenseDate:              record.expenseDate ? dayjs(record.expenseDate) : dayjs(),
    expenseType:              record.expenseType || 'paid',
    categoryId:               record.categoryId || null,
    supplierId:               record.supplierId || null,
    cashBankLedgerAccountId:  record.cashBankLedgerAccountId || null,
    paymentMode:              record.paymentMode || 'bank',
    taxableValue:             Number(record.taxableValue) || 0,
    gstApplicable:            record.gstApplicable === true,
    gstRate:                  Number(record.gstRate) || 0,
    supplyType:               record.supplyType || 'intra_state',
    cgstAmount:               Number(record.cgstAmount) || 0,
    sgstAmount:               Number(record.sgstAmount) || 0,
    igstAmount:               Number(record.igstAmount) || 0,
    itcEligible:              record.itcEligible !== false,
    totalAmount:              Number(record.totalAmount) || 0,
    referenceNo:              record.referenceNo || '',
    remarks:                  record.remarks || ''
  };
}

// Generic server-mode lookup Select. `fetcher(q)` returns the row list;
// `optionRender({row})` returns the JSX for each <Option>. `idKey`
// names the field the row uses as its id.
function ServerSelect({
  fetcher, value, onChange, disabled, placeholder,
  idKey = 'id', renderOption
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const rows = await fetcher(q || '');
      setOptions(Array.isArray(rows) ? rows : []);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, [fetcher]);

  useEffect(() => { load(''); }, [load]);

  const onSearch = useCallback((t) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(t), 200);
  }, [load]);

  return (
    <Select
      style={{ width: '100%' }}
      placeholder={placeholder}
      showSearch
      disabled={disabled}
      value={value || undefined}
      onChange={(v) => onChange(v)}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      notFoundContent={loading ? 'Loading…' : 'No results'}
      options={options.map((r) => ({ value: r[idKey], label: renderOption(r) }))}
    />
  );
}

export default function ExpenseEntryModal({
  open, mode, record, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [expenseType, setExpenseType] = useState('paid');
  const [gstApplicable, setGstApplicable] = useState(false);
  const [supplyType, setSupplyType] = useState('intra_state');
  const [taxableValue, setTaxableValue] = useState(0);
  const [gstRate, setGstRate] = useState(0);
  const [cgst, setCgst] = useState(0);
  const [sgst, setSgst] = useState(0);
  const [igst, setIgst] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [categoryId, setCategoryId] = useState(null);
  const [supplierId, setSupplierId] = useState(null);
  const [cashBankLedgerId, setCashBankLedgerId] = useState(null);

  // Label caches so the posting preview can render party / ledger
  // names without re-querying on every render.
  const partyCacheRef = useRef(new Map());
  const ledgerCacheRef = useRef(new Map());
  const categoryCacheRef = useRef(new Map());

  const isPosted   = record && READONLY_STATUSES.has(record.status);
  const readOnly   = mode === 'view' || isPosted;
  const status     = record ? record.status : 'draft';

  // Reset form on open.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const d = defaultsFor(mode, record);
    form.setFieldsValue(d);
    setExpenseType(d.expenseType);
    setGstApplicable(d.gstApplicable);
    setSupplyType(d.supplyType);
    setTaxableValue(d.taxableValue);
    setGstRate(d.gstRate);
    setCgst(d.cgstAmount); setSgst(d.sgstAmount); setIgst(d.igstAmount);
    setTotalAmount(d.totalAmount);
    setCategoryId(d.categoryId);
    setSupplierId(d.supplierId);
    setCashBankLedgerId(d.cashBankLedgerAccountId);
  }, [open, mode, record, form]);

  // Seed caches with the record's joined display fields so the
  // preview renders names immediately on edit/view.
  useEffect(() => {
    if (!record) return;
    if (record.categoryId && record.categoryName) {
      categoryCacheRef.current.set(record.categoryId, {
        name: record.categoryName, code: record.categoryCode
      });
    }
    if (record.supplierId && record.supplierName) {
      partyCacheRef.current.set(record.supplierId, {
        name: record.supplierName, code: record.supplierCode
      });
    }
    if (record.cashBankLedgerAccountId && record.cashBankLedgerName) {
      ledgerCacheRef.current.set(record.cashBankLedgerAccountId, {
        name: record.cashBankLedgerName, code: record.cashBankLedgerCode
      });
    }
    if (record.expenseLedgerAccountId && record.expenseLedgerName) {
      ledgerCacheRef.current.set(record.expenseLedgerAccountId, {
        name: record.expenseLedgerName, code: record.expenseLedgerCode
      });
    }
  }, [record]);

  // Auto-compute GST split + total whenever inputs change.
  useEffect(() => {
    if (!gstApplicable || readOnly) return;
    if (taxableValue == null || taxableValue <= 0 || gstRate == null || gstRate <= 0) {
      setCgst(0); setSgst(0); setIgst(0);
      const t = Number(taxableValue) || 0;
      setTotalAmount(Math.round(t * 100) / 100);
      form.setFieldsValue({ cgstAmount: 0, sgstAmount: 0, igstAmount: 0, totalAmount: t });
      return;
    }
    // Call the same helper the backend uses to keep arithmetic
    // identical. Server endpoint is read-only and idempotent.
    expenseEntryApi.calculateTax({
      gstApplicable: true,
      taxableValue: Number(taxableValue),
      gstRate: Number(gstRate),
      supplyType
    }).then((r) => {
      setCgst(r.cgstAmount); setSgst(r.sgstAmount); setIgst(r.igstAmount);
      setTotalAmount(r.totalAmount);
      form.setFieldsValue({
        cgstAmount: r.cgstAmount,
        sgstAmount: r.sgstAmount,
        igstAmount: r.igstAmount,
        totalAmount: r.totalAmount
      });
    }).catch(() => {
      // Fall back to local computation so the modal still functions
      // even if the API call hiccups.
      const t = Number(taxableValue) || 0;
      const r = Number(gstRate) || 0;
      const tax = Math.round(t * (r / 100) * 100) / 100;
      if (supplyType === 'intra_state') {
        const c = Math.round((tax / 2) * 100) / 100;
        const s = Math.round((tax - c) * 100) / 100;
        setCgst(c); setSgst(s); setIgst(0);
        setTotalAmount(Math.round((t + tax) * 100) / 100);
        form.setFieldsValue({ cgstAmount: c, sgstAmount: s, igstAmount: 0, totalAmount: t + tax });
      } else {
        setCgst(0); setSgst(0); setIgst(tax);
        setTotalAmount(Math.round((t + tax) * 100) / 100);
        form.setFieldsValue({ cgstAmount: 0, sgstAmount: 0, igstAmount: tax, totalAmount: t + tax });
      }
    });
  }, [gstApplicable, taxableValue, gstRate, supplyType, readOnly, form]);

  // When GST is turned OFF, zero the split and let total ride taxable.
  useEffect(() => {
    if (gstApplicable || readOnly) return;
    setCgst(0); setSgst(0); setIgst(0);
    const t = Number(taxableValue) || 0;
    setTotalAmount(Math.round(t * 100) / 100);
    form.setFieldsValue({ cgstAmount: 0, sgstAmount: 0, igstAmount: 0, totalAmount: t });
  }, [gstApplicable, taxableValue, readOnly, form]);

  // Posting preview construction.
  const preview = useMemo(() => {
    const lines = [];
    const category   = categoryId ? categoryCacheRef.current.get(categoryId)         : null;
    const supplier   = supplierId ? partyCacheRef.current.get(supplierId)            : null;
    const cashBank   = cashBankLedgerId ? ledgerCacheRef.current.get(cashBankLedgerId) : null;
    const expenseDr  = gstApplicable ? Number(taxableValue) : Number(totalAmount);
    lines.push({
      side: 'Dr',
      label: category ? `${category.code || ''} ${category.name || ''}`.trim() : 'Expense ledger',
      amount: Math.round(expenseDr * 100) / 100
    });
    if (gstApplicable) {
      if (cgst > 0) lines.push({ side: 'Dr', label: 'CGST Input', amount: cgst });
      if (sgst > 0) lines.push({ side: 'Dr', label: 'SGST Input', amount: sgst });
      if (igst > 0) lines.push({ side: 'Dr', label: 'IGST Input', amount: igst });
    }
    if (expenseType === 'paid') {
      lines.push({
        side: 'Cr',
        label: cashBank ? `${cashBank.code || ''} ${cashBank.name || ''}`.trim() : 'Cash / Bank ledger',
        amount: Number(totalAmount)
      });
    } else {
      lines.push({
        side: 'Cr',
        label: supplier ? `${supplier.code || ''} ${supplier.name || ''}`.trim() : 'Supplier ledger',
        amount: Number(totalAmount)
      });
    }
    const totalDr = lines.filter((l) => l.side === 'Dr').reduce((a, l) => a + l.amount, 0);
    const totalCr = lines.filter((l) => l.side === 'Cr').reduce((a, l) => a + l.amount, 0);
    return {
      lines,
      totalDr: Math.round(totalDr * 100) / 100,
      totalCr: Math.round(totalCr * 100) / 100,
      balanced: Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0
    };
  }, [categoryId, supplierId, cashBankLedgerId, expenseType, gstApplicable,
      taxableValue, totalAmount, cgst, sgst, igst]);

  // Wire lookups via the API client.
  const fetchCategories = useCallback(async (q) => {
    const rows = await expenseEntryApi.listCategories(q);
    for (const r of rows) {
      categoryCacheRef.current.set(r.id, { name: r.categoryName, code: r.categoryCode });
    }
    return rows;
  }, []);
  const fetchSuppliers = useCallback(async (q) => {
    const rows = await expenseEntryApi.supplierLookup(q || '', 100);
    for (const r of rows) {
      partyCacheRef.current.set(r.id, { name: r.supplierName, code: r.supplierCode });
    }
    return rows;
  }, []);
  const fetchCashBank = useCallback(async (q) => {
    const rows = await expenseEntryApi.cashBankLedgerLookup(q || '', 100);
    for (const r of rows) {
      ledgerCacheRef.current.set(r.id, { name: r.accountName, code: r.accountCode });
    }
    return rows;
  }, []);

  const saveDraft = useCallback(async () => {
    try {
      const v = await form.validateFields();
      setBusy(true);
      const payload = {
        expenseDate:              v.expenseDate.format('YYYY-MM-DD'),
        expenseType:              v.expenseType,
        categoryId:               v.categoryId,
        supplierId:               v.expenseType === 'credit' ? v.supplierId : (v.supplierId || null),
        cashBankLedgerAccountId:  v.expenseType === 'paid'   ? v.cashBankLedgerAccountId : null,
        paymentMode:              v.expenseType === 'paid'   ? v.paymentMode : null,
        taxableValue:             Number(v.taxableValue) || 0,
        gstApplicable:            !!v.gstApplicable,
        gstRate:                  Number(v.gstRate) || 0,
        supplyType:               v.supplyType,
        cgstAmount:               Number(v.cgstAmount) || 0,
        sgstAmount:               Number(v.sgstAmount) || 0,
        igstAmount:               Number(v.igstAmount) || 0,
        itcEligible:              v.itcEligible !== false,
        totalAmount:              Number(v.totalAmount) || 0,
        referenceNo:              v.referenceNo || null,
        remarks:                  v.remarks || null
      };
      let saved;
      if (mode === 'add') {
        saved = await expenseEntryApi.createDraft(payload);
        message.success(`Draft ${saved.expenseNo} saved.`);
      } else if (mode === 'edit') {
        const { expenseType: _, ...patch } = payload;  // type is immutable once draft exists
        saved = await expenseEntryApi.updateDraft(record.id, patch);
        message.success('Draft updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(e?.response?.data?.error?.message || 'Save failed.');
    } finally { setBusy(false); }
  }, [form, mode, record, onSaved]);

  const title = useMemo(() => {
    const en = record && record.expenseNo ? ' — ' + record.expenseNo : '';
    if (mode === 'add')  return 'New Expense Entry';
    if (mode === 'view') return `View Expense${en}`;
    return `Edit Expense${en}`;
  }, [mode, record]);

  const footer = (
    <Space>
      {!readOnly ? (
        <Button type="primary" icon={<SaveOutlined />} loading={busy}
          disabled={!preview.balanced}
          onClick={saveDraft}>
          {mode === 'add' ? 'Save Draft' : 'Save Changes'}
        </Button>
      ) : null}
      <Button onClick={onClose}>Close</Button>
    </Space>
  );

  // Stage UI-FIX (Expense Entry focus loss): `function DetailsBody()`
  // was declared inside this parent component's body, so every parent
  // re-render produced a brand new function identity → React treated
  // `<DetailsBody />` as a different component type each render and
  // remounted the entire form, dropping focus on every keystroke and
  // flashing pending validation messages. Converting to a JSX const
  // keeps the underlying element types stable so React patches in
  // place. All hooks, validation, GST calc and save flow unchanged.
  const detailsBody = (<>

      <Form className="erp-accounts-form" form={form} layout="vertical" preserve initialValues={emptyDefaults()}>
        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Expense No">
              <Input value={record?.expenseNo || (mode === 'add' ? '(auto-generated)' : '')} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Date" name="expenseDate"
              rules={[{ required: true, message: 'Date is required' }]}>
              <DatePicker style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Type" name="expenseType"
              rules={[{ required: true, message: 'Type is required' }]}>
              <Radio.Group
                disabled={readOnly || mode === 'edit'}
                onChange={(e) => setExpenseType(e.target.value)}>
                <Radio.Button value="paid">Paid</Radio.Button>
                <Radio.Button value="credit">Credit</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Reference / Bill No" name="referenceNo">
              <Input maxLength={100} disabled={readOnly}
                placeholder="Vendor invoice / receipt no" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Category" name="categoryId"
              rules={[{ required: true, message: 'Category is required' }]}>
              <ServerSelect
                fetcher={fetchCategories}
                value={categoryId}
                disabled={readOnly}
                placeholder="Select expense category…"
                renderOption={(r) => (
                  <span>
                    <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{r.categoryCode}</code>
                    {r.categoryName}
                    {r.gstApplicable ? <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>· GST</span> : null}
                  </span>
                )}
                onChange={(v) => { setCategoryId(v); form.setFieldsValue({ categoryId: v }); }}
              />
            </Form.Item>
          </Col>
          {expenseType === 'paid' ? (
            <>
              <Col xs={24} md={6}>
                <Form.Item label="Payment Mode" name="paymentMode"
                  rules={[{ required: true, message: 'Payment mode is required for paid expenses' }]}>
                  <Select disabled={readOnly}
                    options={PAYMENT_MODES.map((m) => ({ value: m.value, label: m.label }))} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Cash / Bank Ledger" name="cashBankLedgerAccountId"
                  rules={[{ required: true, message: 'Cash/Bank ledger is required for paid expenses' }]}>
                  <ServerSelect
                    fetcher={fetchCashBank}
                    value={cashBankLedgerId}
                    disabled={readOnly}
                    placeholder="Select cash/bank ledger…"
                    renderOption={(r) => (
                      <span>
                        <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{r.accountCode}</code>
                        {r.accountName}
                        <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>· {r.accountGroup}</span>
                      </span>
                    )}
                    onChange={(v) => { setCashBankLedgerId(v); form.setFieldsValue({ cashBankLedgerAccountId: v }); }}
                  />
                </Form.Item>
              </Col>
            </>
          ) : (
            <Col xs={24} md={12}>
              <Form.Item label="Supplier" name="supplierId"
                rules={[{ required: true, message: 'Supplier is required for credit expenses' }]}>
                <ServerSelect
                  fetcher={fetchSuppliers}
                  value={supplierId}
                  disabled={readOnly}
                  placeholder="Select supplier…"
                  renderOption={(r) => (
                    <span>
                      <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{r.supplierCode}</code>
                      {r.supplierName}
                      {r.gstin ? <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>· {r.gstin}</span> : null}
                    </span>
                  )}
                  onChange={(v) => { setSupplierId(v); form.setFieldsValue({ supplierId: v }); }}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Taxable Value (₹)" name="taxableValue"
              rules={[{ type: 'number', min: 0, message: 'Must be ≥ 0' }]}>
              <InputNumber
                style={{ width: '100%' }} min={0} step={1} precision={2}
                disabled={readOnly}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0}
                onChange={(n) => setTaxableValue(Number(n) || 0)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="GST?" name="gstApplicable" valuePropName="checked">
              <Switch disabled={readOnly}
                onChange={(v) => setGstApplicable(v)} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="GST Rate (%)" name="gstRate">
              <Select disabled={readOnly || !gstApplicable}
                onChange={(v) => setGstRate(Number(v) || 0)}
                options={GST_RATES.map((r) => ({ value: r, label: `${r}%` }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Supply Type" name="supplyType">
              <Select disabled={readOnly || !gstApplicable}
                onChange={(v) => setSupplyType(v)}
                options={SUPPLY_TYPES.map((s) => ({ value: s.value, label: s.label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="ITC Eligible" name="itcEligible" valuePropName="checked">
              <Switch disabled={readOnly || !gstApplicable} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={4}>
            <Form.Item label="CGST (₹)" name="cgstAmount">
              <InputNumber style={{ width: '100%' }} disabled precision={2} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="SGST (₹)" name="sgstAmount">
              <InputNumber style={{ width: '100%' }} disabled precision={2} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="IGST (₹)" name="igstAmount">
              <InputNumber style={{ width: '100%' }} disabled precision={2} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Total Amount (₹)" name="totalAmount"
              rules={[{ type: 'number', min: 0.01, message: 'Total must be > 0' }]}>
              <InputNumber style={{ width: '100%' }}
                disabled={readOnly || gstApplicable}
                min={0.01} step={1} precision={2}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0}
                onChange={(n) => setTotalAmount(Number(n) || 0)} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000} disabled={readOnly}
            placeholder="Narration for this expense entry" />
        </Form.Item>
      </Form>

      {/* Posting preview */}
      <div className="erp-accounts-preview">
        <div className="erp-accounts-preview__title">
          Posting preview
          <Tag color={EXPENSE_TYPE_COLOR[expenseType]} style={{ marginInlineStart: 8 }}>
            {EXPENSE_TYPE_LABEL[expenseType]}
          </Tag>
        </div>
        <table>
          <thead>
            <tr>
              <th style={{ width: 56 }}>Side</th>
              <th>Ledger</th>
              <th style={{ textAlign: 'right', width: 140 }}>Debit (₹)</th>
              <th style={{ textAlign: 'right', width: 140 }}>Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {preview.lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <Tag color={l.side === 'Dr' ? 'blue' : 'magenta'} style={{ marginInlineEnd: 0 }}>{l.side}</Tag>
                </td>
                <td>{l.label}</td>
                <td style={{ textAlign: 'right' }}>
                  {l.side === 'Dr' ? formatINR(l.amount) : <Text type="secondary">—</Text>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {l.side === 'Cr' ? formatINR(l.amount) : <Text type="secondary">—</Text>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{ textAlign: 'right' }}>Total</td>
              <td style={{ textAlign: 'right' }}>₹ {formatINR(preview.totalDr)}</td>
              <td style={{ textAlign: 'right' }}>₹ {formatINR(preview.totalCr)}</td>
            </tr>
            <tr>
              <td colSpan={2} style={{ textAlign: 'right' }} className="erp-accounts-preview__sub">Difference</td>
              <td colSpan={2} style={{ textAlign: 'right',
                  color: preview.balanced ? '#3f8600' : '#cf1322', fontWeight: 600 }}>
                ₹ {formatINR(Math.abs(preview.totalDr - preview.totalCr))} {preview.balanced
                  ? <CheckOutlined /> : <CloseCircleOutlined />}
              </td>
            </tr>
          </tfoot>
        </table>
        {!preview.balanced ? (
          <Alert
            type="info" showIcon style={{ marginTop: 10 }}
            message="Save Draft is disabled until the entry is balanced (debit total = credit total > 0)." />
        ) : null}
      </div>

      {status === 'cancelled' && record && record.cancelReason ? (
        <Alert type="error" showIcon style={{ marginTop: 12 }}
          message="Cancelled expense"
          description={<><b>Reason:</b> {record.cancelReason}</>} />
      ) : null}
    </>);

  return (
    <Modal
      title={
        <span>
          {title}
          {record && record.status ? (
            <Tag color={STATUS_COLOR[record.status] || 'default'}
                 style={{ marginInlineStart: 12 }}>
              {(STATUS_LABEL[record.status] || record.status).toUpperCase()}
            </Tag>
          ) : null}
          {record && record.voucherNo ? (
            <Tag color="blue" style={{ marginInlineStart: 8 }}>
              JV {record.voucherNo}
            </Tag>
          ) : null}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={1080}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      {/* Stage 6B.7D — Audit + Attachments tabs only in view mode. */}
      {mode === 'view' && record && record.id ? (
        <Tabs size="small" defaultActiveKey="details" items={[
          { key: 'details',     label: 'Details',     children: detailsBody },
          { key: 'audit',       label: 'Audit',
            children: (
              <AuditLogPanel entityType="expense" entityId={record.id} />
            ) },
          { key: 'attachments', label: 'Attachments',
            children: (
              <AttachmentsPanel entityType="expense" entityId={record.id}
                canUpload canDelete />
            ) }
        ]} />
      ) : (
        detailsBody
      )}
    </Modal>
  );
}
